import React, { createContext, useContext, useEffect, useState } from 'react';
import { translations, Language, Translations } from 'i18n';

type LanguageProviderProps = {
  children: React.ReactNode;
  storageKey?: string;
};

type LanguageProviderState = {
  language: Language;
  setLanguage: (language: Language) => void;
  t: Translations;
};

const LanguageProviderContext = createContext<LanguageProviderState | undefined>(undefined);

const isLanguage = (value: unknown): value is Language =>
  typeof value === 'string' && value in translations;

const detectLanguage = (storageKey: string): Language => {
  try {
    const saved = localStorage.getItem(storageKey);
    if (isLanguage(saved)) return saved;
  } catch {
    // storage blocked (private mode) — fall through to browser language
  }
  return navigator.language?.toLowerCase().startsWith('fr') ? 'fr' : 'en';
};

export function LanguageProvider({ children, storageKey = 'portfolio-lang' }: LanguageProviderProps) {
  const [language, setLanguageState] = useState<Language>(() => detectLanguage(storageKey));

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (next: Language) => {
    try {
      localStorage.setItem(storageKey, next);
    } catch {
      // storage blocked — choice still applies for this visit
    }
    setLanguageState(next);
  };

  return (
    <LanguageProviderContext.Provider value={{ language, setLanguage, t: translations[language] }}>
      {children}
    </LanguageProviderContext.Provider>
  );
}

export const useLanguage = () => {
  const context = useContext(LanguageProviderContext);

  if (context === undefined)
    throw new Error('useLanguage must be used within a LanguageProvider');

  return context;
};
